import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { Search, ArrowRight, ShieldCheck, Wallet, Repeat } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORY_ICONS = {
  "Power Tools": "🔧", "Hand Tools": "🔨", "Gardening": "🌿", "Electronics": "🔌",
};

export default function Home() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [listings, setListings] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/browse?search=${encodeURIComponent(query)}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-16 lg:pt-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-5">
              Peer-to-peer rentals
            </span>
            <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tighter leading-[0.95]">
              Rent the gear.<br />
              <span className="text-[#FF5A00]">Own the project.</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-6 max-w-md leading-relaxed">
              Borrow drills, ladders, and power tools from people nearby — or list your own and earn while it sits in the garage.
            </p>

            <form onSubmit={doSearch} className="mt-8 flex items-center gap-2 bg-white border border-black/15 rounded-full p-1.5 max-w-md shadow-sm">
              <Search className="w-5 h-5 ml-3 text-muted-foreground shrink-0" />
              <input
                data-testid="hero-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="What do you need? e.g. circular saw"
                className="flex-1 bg-transparent outline-none text-sm px-1 min-w-0"
              />
              <button data-testid="hero-search-btn" className="bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 shrink-0">
                Search
              </button>
            </form>

            <div className="flex flex-wrap gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2 text-muted-foreground"><ShieldCheck className="w-4 h-4 text-[#2E7D32]" /> Verified renters</span>
              <span className="flex items-center gap-2 text-muted-foreground"><Wallet className="w-4 h-4 text-[#2E7D32]" /> Secure payments</span>
              <span className="flex items-center gap-2 text-muted-foreground"><Repeat className="w-4 h-4 text-[#2E7D32]" /> Flexible dates</span>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <motion.img
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                src="https://images.unsplash.com/photo-1590880795696-20c7dfadacde?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Workshop" className="rounded-2xl object-cover w-full h-64 row-span-2 border border-black/10"
              />
              <motion.img
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
                src="https://images.unsplash.com/photo-1546827209-a218e99fdbe9?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Drills" className="rounded-2xl object-cover w-full h-[124px] border border-black/10"
              />
              <motion.img
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                src="https://images.unsplash.com/photo-1518709414768-a88981a4515d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Cordless drill" className="rounded-2xl object-cover w-full h-[124px] border border-black/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-14">
        <div className="flex flex-wrap gap-2.5">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`cat-chip-${c}`}
              onClick={() => navigate(`/browse?category=${encodeURIComponent(c)}`)}
              className="px-4 py-2 rounded-full border border-black/15 bg-white text-sm font-medium hover:border-black hover:bg-black hover:text-white transition-colors"
            >
              {CATEGORY_ICONS[c] ? `${CATEGORY_ICONS[c]} ` : ""}{c}
            </button>
          ))}
        </div>
      </section>

      {/* Beyond tools — outdoor gear */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <div className="grid md:grid-cols-2 gap-5">
          <button
            onClick={() => navigate("/browse?search=camping")}
            data-testid="outdoor-camping-card"
            className="group relative overflow-hidden rounded-2xl border border-black/10 h-64 text-left"
          >
            <img
              src="https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Camping gear" loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF9A5A]">Outdoors</span>
              <h3 className="font-display text-2xl font-bold text-white tracking-tight mt-1">Camping gear</h3>
              <p className="text-white/85 text-sm mt-1 flex items-center gap-1">Tents, stoves & sleeping bags <ArrowRight className="w-4 h-4" /></p>
            </div>
          </button>

          <button
            onClick={() => navigate("/browse?search=cycling")}
            data-testid="outdoor-cycling-card"
            className="group relative overflow-hidden rounded-2xl border border-black/10 h-64 text-left"
          >
            <img
              src="https://images.unsplash.com/photo-1632050592122-6b730e1ac63f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Cycling gear" loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF9A5A]">Outdoors</span>
              <h3 className="font-display text-2xl font-bold text-white tracking-tight mt-1">Cycling & bikes</h3>
              <p className="text-white/85 text-sm mt-1 flex items-center gap-1">Mountain bikes, helmets & racks <ArrowRight className="w-4 h-4" /></p>
            </div>
          </button>
        </div>
      </section>

      {/* Listings */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-3xl font-bold tracking-tight">Available near you</h2>
          <button onClick={() => navigate("/browse")} data-testid="view-all-btn" className="flex items-center gap-1 text-sm font-semibold hover:text-[#FF5A00] transition-colors">
            View all <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {listings.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-black/10">
            <p className="text-muted-foreground">No items listed yet. Be the first to list your gear!</p>
            <button onClick={() => navigate("/create")} className="mt-4 bg-[#0A0A0A] text-white rounded-full px-6 py-3 font-medium hover:bg-[#FF5A00] transition-colors">List an item</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.slice(0, 8).map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
